import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import { LoadingSpinner, EmptyState } from '../components';

// ─── FAQ Section ─────────────────────────────────────────────────────────────
const DEFAULT_FAQS = [
  {
    category: 'Orders',
    items: [
      { q: 'How do I place an order?', a: 'Browse kitchens, add items to cart, select a pickup time, and checkout.' },
      { q: 'Can I cancel my order?', a: 'Orders can be cancelled while in "Placed" status. Go to Order Details and tap Cancel.' },
      { q: 'How do I track my order?', a: 'Visit My Orders to see real-time status updates.' },
    ],
  },
  {
    category: 'Payment',
    items: [
      { q: 'What payment methods are accepted?', a: 'We accept online payments via Stripe (card) and Pay on Pickup (cash/card).' },
      { q: 'Is my payment information secure?', a: 'Yes, we use Stripe for secure payment processing. We never store your card details.' },
    ],
  },
  {
    category: 'Account',
    items: [
      { q: 'How do I update my profile?', a: 'Go to Profile > Edit Profile to update your name and email.' },
      { q: 'How do I change my phone number?', a: 'Phone number changes require contacting support.' },
    ],
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-question">
        <span>{q}</span>
        <span className="faq-toggle">{open ? '−' : '+'}</span>
      </div>
      {open && <p className="faq-answer">{a}</p>}
    </div>
  );
};

const SupportPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('faq'); // 'faq' | 'tickets'
  const [faqs, setFaqs] = useState(DEFAULT_FAQS);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', category: 'general' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiService.getSupportFAQs().then(res => {
      if (res?.success && res.data?.categories) setFaqs(res.data.categories);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'tickets' && isAuthenticated) loadTickets();
  }, [activeTab, isAuthenticated]);

  const loadTickets = async () => {
    setTicketsLoading(true);
    try {
      const res = await apiService.getSupportTickets({ per_page: 20 });
      const list = res?.data?.tickets || res?.data || [];
      setTickets(Array.isArray(list) ? list : []);
    } catch (e) {}
    setTicketsLoading(false);
  };

  const handleSubmitTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      alert('Please fill in subject and message');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiService.createSupportTicket(newTicket);
      if (res?.success !== false) {
        setShowNewTicket(false);
        setNewTicket({ subject: '', message: '', category: 'general' });
        if (isAuthenticated) loadTickets();
        alert('Ticket submitted! We will respond within 24 hours.');
      } else {
        alert(res?.error || 'Failed to submit ticket');
      }
    } catch (e) {
      alert('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const STATUS_COLOR = { open: '#3B82F6', in_progress: '#F59E0B', resolved: '#10B981', closed: '#888' };

  return (
    <div className="support-page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/customer/profile')}>← Back</button>
        <h2 className="page-title">🆘 Help & Support</h2>
      </div>

      {/* Tab Switch */}
      <div className="support-tabs">
        <button className={`tab-btn ${activeTab === 'faq' ? 'active' : ''}`} onClick={() => setActiveTab('faq')}>FAQs</button>
        <button className={`tab-btn ${activeTab === 'tickets' ? 'active' : ''}`} onClick={() => setActiveTab('tickets')}>My Tickets</button>
      </div>

      {activeTab === 'faq' ? (
        <div className="faq-section">
          {faqs.map((cat, idx) => (
            <div key={idx} className="faq-category">
              <h3 className="faq-category-title">{cat.category}</h3>
              {(cat.items || cat.questions || []).map((item, i) => (
                <FAQItem key={i} q={item.q || item.question} a={item.a || item.answer} />
              ))}
            </div>
          ))}

          <div className="support-contact-card">
            <h3>Still need help?</h3>
            <p>Our support team is available Mon–Fri 9am–6pm</p>
            <button className="btn-primary" onClick={() => { if (!isAuthenticated) { navigate('/customer/login'); return; } setShowNewTicket(true); }}>
              📧 Create Support Ticket
            </button>
          </div>
        </div>
      ) : (
        <div className="tickets-section">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn-primary" onClick={() => { if (!isAuthenticated) { navigate('/customer/login'); return; } setShowNewTicket(true); }}>
              + New Ticket
            </button>
          </div>
          {!isAuthenticated ? (
            <div className="empty-state">
              <p>Please log in to view your support tickets</p>
              <button className="btn-primary" onClick={() => navigate('/customer/login')}>Login</button>
            </div>
          ) : ticketsLoading ? (
            <LoadingSpinner />
          ) : tickets.length === 0 ? (
            <EmptyState icon="🎫" title="No tickets" subtitle="Create a ticket if you need help" />
          ) : (
            <div className="tickets-list">
              {tickets.map(ticket => (
                <div key={ticket.id} className="ticket-card" onClick={() => navigate(`/customer/support/tickets/${ticket.id}`)}>
                  <div className="ticket-card-header">
                    <span className="ticket-subject">{ticket.subject}</span>
                    <span className="ticket-status" style={{ color: STATUS_COLOR[ticket.status] || '#888' }}>
                      {ticket.status?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="ticket-preview">{ticket.message?.slice(0, 80)}...</p>
                  <p className="ticket-date">{new Date(ticket.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="modal-overlay" onClick={() => setShowNewTicket(false)}>
          <div className="modal-container modal-medium" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New Support Ticket</h3>
              <button className="modal-close" onClick={() => setShowNewTicket(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-input" value={newTicket.category} onChange={e => setNewTicket(d => ({ ...d, category: e.target.value }))}>
                  <option value="general">General</option>
                  <option value="order">Order Issue</option>
                  <option value="payment">Payment</option>
                  <option value="account">Account</option>
                  <option value="feedback">Feedback</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <input type="text" className="form-input" placeholder="Brief description" value={newTicket.subject} onChange={e => setNewTicket(d => ({ ...d, subject: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea className="form-input" rows={5} placeholder="Describe your issue in detail..." value={newTicket.message} onChange={e => setNewTicket(d => ({ ...d, message: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button className="btn-outline" onClick={() => setShowNewTicket(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleSubmitTicket} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
